/**
 * TrialsScreen Component
 *
 * Main screen for the Elite 4 Trials mode.
 * Displays region banners for each generation's Elite 4 challenge.
 * Players must defeat the career Elite 4 to unlock trials.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Flame,
  Lock,
  Trophy,
  ChevronRight,
  Shield,
  Award
} from 'lucide-react';
import { useGame } from '../contexts/GameContext';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchTrialData, getTrialRegions, getTrialRegionKeys, getTrialRewards } from '../data/elite4Trials';
import { TYPE_COLORS, mapToGameType } from '../components/TypeIcon';
import { apiGetUserStamps } from '../services/apiService';
import StampBook from '../components/StampBook';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Region banner card component
const RegionBanner = ({ region, regionKey, isUnlocked, hasStamp, stampData, onClick }) => {
  const typeColor = region.color || '#6B7280';

  return (
    <motion.button
      variants={itemVariants}
      whileHover={isUnlocked ? { scale: 1.02, y: -4 } : {}}
      whileTap={isUnlocked ? { scale: 0.98 } : {}}
      onClick={() => isUnlocked && onClick()}
      disabled={!isUnlocked}
      className={`relative w-full h-28 rounded-xl overflow-hidden shadow-lg transition-all ${
        !isUnlocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      }`}
      style={{
        background: `linear-gradient(135deg, ${typeColor}, ${typeColor}CC)`
      }}
    >
      {/* Left side - Region name and status (positioned absolutely) */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-left z-20">
        <h3 className="text-2xl font-bold text-white tracking-wide drop-shadow-lg">{region.name}</h3>
        <p className="text-white/80 text-sm font-medium">{region.region}</p>
        {hasStamp && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-300 mt-1 font-semibold">
            <Trophy size={12} />
            Complete
          </span>
        )}
      </div>

      {/* Slashed trainer sprites - spanning from left text area to right edge */}
      <div className="absolute right-0 top-0 h-full flex items-center" style={{ left: '100px' }}>
        {region.members.map((member, idx) => {
          const config = member.spriteConfig || { scale: 2.5, offsetX: 0, offsetY: 0 };
          // Calculate width to distribute sprites across the container
          // Each sprite takes ~25% of remaining width with overlap
          return (
            <div
              key={idx}
              className="relative h-full overflow-hidden"
              style={{
                flex: '1 1 0',
                minWidth: '60px',
                marginLeft: idx === 0 ? 0 : '-20px',
                zIndex: idx,
                clipPath: 'polygon(30% 0%, 100% 0%, 70% 100%, 0% 100%)',
                transform: 'skewX(-12deg)'
              }}
            >
              <div
                className="absolute inset-0 border-l-2 border-white/40"
                style={{
                  backgroundColor: TYPE_COLORS[mapToGameType(member.type)] || '#6B7280'
                }}
              >
                {member.sprite && (
                  <img
                    src={member.sprite}
                    alt={member.name}
                    className="absolute"
                    style={{
                      transform: `skewX(12deg) scale(${config.scale})`,
                      top: `calc(50% + ${config.offsetY}px)`,
                      left: `calc(50% + ${config.offsetX}px)`,
                      transformOrigin: 'center center',
                      width: '100%',
                      height: 'auto'
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Arrow indicator - overlaid on right edge */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/30 rounded-full p-2 z-10 backdrop-blur-sm">
        <ChevronRight size={18} className="text-white" />
      </div>

      {/* Lock overlay */}
      {!isUnlocked && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
          <div className="text-center">
            <Lock size={28} className="text-white/70 mx-auto mb-1" />
            <p className="text-white/80 text-xs font-semibold">Defeat Career Elite 4</p>
          </div>
        </div>
      )}
    </motion.button>
  );
};

const TrialsScreen = () => {
  const { setGameState, setTrialState } = useGame();
  const { trainedPokemon } = useInventory();
  const { token } = useAuth();

  const [stamps, setStamps] = useState({});
  const [loading, setLoading] = useState(true);
  const [showStampBook, setShowStampBook] = useState(false);
  const [trialRegions, setTrialRegions] = useState({});
  const [regionKeys, setRegionKeys] = useState([]);
  const [trialRewards, setTrialRewards] = useState({ primos: 1000 });

  // Check if player has defeated career Elite 4 (any Pokemon with 8 gyms defeated = champion)
  const hasDefeatedEliteFour = trainedPokemon.some(p => p.gymsDefeated >= 8);

  // Load trial data and user stamps
  useEffect(() => {
    const loadData = async () => {
      // Fetch trial data from backend
      const trialData = await fetchTrialData();
      if (trialData) {
        setTrialRegions(trialData.regions || {});
        setRegionKeys(trialData.regionKeys || []);
        setTrialRewards(trialData.rewards || { primos: 1000 });
      }

      // Fetch user stamps if authenticated
      if (token) {
        try {
          const data = await apiGetUserStamps(token);
          if (data?.stamps) {
            // Convert array to object keyed by region
            const stampMap = {};
            data.stamps.forEach(s => {
              stampMap[s.region_key] = s;
            });
            setStamps(stampMap);
          }
        } catch (error) {
          console.error('Failed to load stamps:', error);
        }
      }

      setLoading(false);
    };

    loadData();
  }, [token]);

  const handleSelectRegion = (regionKey) => {
    // Set trial state and navigate to trial detail screen
    setTrialState({
      regionKey,
      currentMemberIndex: 0,
      usedSpecies: [],
      inProgress: false
    });
    setGameState('trialDetail');
  };

  const completedCount = Object.keys(stamps).length;
  const totalRegions = regionKeys.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Flame size={48} className="text-red-500 mx-auto mb-4 animate-pulse" />
          <p className="text-pocket-text-light">Loading Trials...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pocket-bg">
      {/* Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 bg-gradient-to-r from-red-600 to-orange-600 shadow-lg"
      >
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setGameState('menu')}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <Flame size={20} className="text-amber-300" />
            <span className="font-bold text-white">Elite 4 Trials</span>
          </div>
          {/* Stamp Book Button */}
          <button
            onClick={() => setShowStampBook(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 rounded-full text-white text-sm font-semibold hover:bg-white/30 transition-colors"
          >
            <Award size={16} className="text-amber-300" />
            <span>{completedCount}/{totalRegions}</span>
          </button>
        </div>
      </motion.header>

      <motion.main
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="p-4 pb-8 max-w-lg mx-auto"
      >
        {/* Info Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl p-5 mb-5 shadow-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Flame size={24} className="text-white" />
            </div>
            <div>
              <h2 className="font-bold text-pocket-text mb-1">Challenge the Elite 4</h2>
              <p className="text-pocket-text-light text-sm">
                Battle the Elite 4 from each region using your Hall of Fame Pokemon.
                Win all 4 battles to earn stamps and {trialRewards.primos.toLocaleString()} Primos!
              </p>
            </div>
          </div>

          {/* Requirements */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Shield size={16} className={hasDefeatedEliteFour ? 'text-green-500' : 'text-gray-400'} />
              <span className={`text-sm ${hasDefeatedEliteFour ? 'text-green-600' : 'text-gray-400'}`}>
                {hasDefeatedEliteFour
                  ? '✓ Elite 4 defeated in Career Mode'
                  : '✗ Defeat the Elite 4 in Career Mode to unlock Trials'}
              </span>
            </div>
          </div>

          {/* Rules */}
          <div className="mt-3 bg-amber-50 rounded-xl p-3 text-xs text-amber-800">
            <p className="font-semibold mb-1">Trial Rules:</p>
            <ul className="space-y-0.5 text-amber-700">
              <li>• Use Hall of Fame Pokemon only</li>
              <li>• Cannot reuse a Pokemon in the same trial</li>
              <li>• If you lose, the trial ends</li>
            </ul>
          </div>
        </motion.div>

        {/* Region Banners */}
        <div className="space-y-4">
          {regionKeys.map((regionKey) => {
            const region = trialRegions[regionKey];
            if (!region) return null;
            const hasStamp = !!stamps[regionKey];

            return (
              <RegionBanner
                key={regionKey}
                region={region}
                regionKey={regionKey}
                isUnlocked={hasDefeatedEliteFour}
                hasStamp={hasStamp}
                stampData={stamps[regionKey]}
                onClick={() => handleSelectRegion(regionKey)}
              />
            );
          })}
        </div>

        {/* Empty State for no trained Pokemon */}
        {trainedPokemon.length === 0 && (
          <motion.div
            variants={itemVariants}
            className="bg-white rounded-2xl p-6 mt-4 shadow-card text-center"
          >
            <Trophy size={48} className="mx-auto mb-3 text-amber-400" />
            <h3 className="font-bold text-pocket-text mb-2">No Hall of Fame Pokemon</h3>
            <p className="text-pocket-text-light text-sm mb-4">
              Complete careers to add Pokemon to your Hall of Fame before attempting Trials.
            </p>
            <button
              onClick={() => setGameState('menu')}
              className="pocket-btn-primary px-6 py-2"
            >
              Back to Menu
            </button>
          </motion.div>
        )}
      </motion.main>

      {/* Stamp Book Modal */}
      <AnimatePresence>
        {showStampBook && (
          <StampBook
            stamps={stamps}
            onClose={() => setShowStampBook(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrialsScreen;
